import React from 'react';
import {
  Box,
  Typography,
  Button,
  Link,
  Divider,
  List,
  ListItem,
  ListItemText,
  Paper,
  Container,
  Chip,
  Grid,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import TitleIcon from '@mui/icons-material/Title';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import SearchIcon from '@mui/icons-material/Search';
import ChecklistIcon from '@mui/icons-material/Checklist';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PsychologyIcon from '@mui/icons-material/Psychology';
import VisibilityIcon from '@mui/icons-material/Visibility';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#282828' },
    success: { main: '#4caf50' },
    warning: { main: '#ff9800' },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    h1: { fontSize: '2.5rem', fontWeight: 700, mb: '1rem', color: '#1976d2' },
    h2: { fontSize: '2rem', fontWeight: 600, m: '2rem 0 1rem' },
    h3: { fontSize: '1.5rem', fontWeight: 500, m: '1.5rem 0 0.8rem' },
    body1: { fontSize: '1.1rem', lineHeight: 1.6, mb: '1rem' },
  },
});

const creativeTips = [
  'Start with a strong keyword to boost SEO',
  'Add emotional triggers like "amazing" or "shocking"',
  'Use numbers to promise value (e.g., "Top 5 Ways...")',
  'Keep it under 60 characters to avoid truncation',
  'Ask a question to pique curiosity',
  'Include power words like "Ultimate", "Essential", "Proven"',
  'Create urgency with words like "Now", "Today", "Quick"',
  'Use brackets to add context (e.g., "[2024 Guide]")',
];

const titleCaseStudies = [
  {
    before: 'How to Make Pancakes',
    after: '3 Fluffy Pancake Recipes That Will Blow Your Mind (Works Every Time!)',
    improvement: '300% more clicks'
  },
  {
    before: 'Gaming PC Build',
    after: 'Building a $1500 Gaming PC That Crushes 4K Gaming [Step-by-Step]',
    improvement: '2.5x higher CTR'
  },
  {
    before: 'Yoga for Beginners',
    after: '15-Minute Morning Yoga Routine That Changed My Life (Beginner Friendly)',
    improvement: '400% more views'
  }
];

const YouTubeTitleGeneratorBlog = () => (
  <ThemeProvider theme={theme}>
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: { xs: 3, md: 5 }, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <TitleIcon color="primary" sx={{ fontSize: 40, mr: 2 }} />
          <Typography variant="h1">The Ultimate YouTube Title Generator: Craft Titles That Rank and Convert</Typography>
        </Box>

        <Typography variant="body1" sx={{ mb: 3 }}>
          In YouTube's competitive landscape, your video title is your first and often only chance to capture attention. Our AI-powered YouTube Title Generator combines data science with proven copywriting techniques to create titles that perform. Whether you're a beginner or seasoned creator, this comprehensive guide will transform how you approach video titling.
        </Typography>

        <Box sx={{ 
          backgroundColor: '#e3f2fd',
          p: 3,
          borderRadius: 2,
          textAlign: 'center',
          mb: 4,
          borderLeft: '4px solid #1976d2',
          boxShadow: '0 4px 20px rgba(25, 118, 210, 0.15)'
        }}>
          <Typography variant="h3" sx={{ mt: 0, color: 'primary.main' }}>
            Generate High-Converting Titles in Seconds
          </Typography>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Our tool analyzes millions of high-performing titles to give you data-backed suggestions
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            href="https://www.myfreshtools.com/youtube/video-title-generator" 
            target="_blank"
            rel="noopener noreferrer" 
            sx={{ 
              mt: 1,
              fontSize: '1.1rem',
              py: 1.5,
              px: 4,
              fontWeight: 600,
              textTransform: 'none',
              borderRadius: 2
            }} 
            color="primary"
            startIcon={<TitleIcon />}
          >
            Try Our Title Generator Now
          </Button>
        </Box>

        <Divider sx={{ my: 4 }} />

        <Typography variant="h2">The Psychology Behind Effective YouTube Titles</Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <PsychologyIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h3" sx={{ fontSize: '1.3rem', mt: 0 }}>Cognitive Triggers</Typography>
              </Box>
              <Typography variant="body1">
                The human brain responds to specific triggers in titles:
              </Typography>
              <List dense>
                {['Curiosity gaps ("You Won\'t Believe What Happened Next")',
                  'Specific numbers ("7 Scientifically Proven Methods")',
                  'Urgency ("Limited Time Offer")',
                  'Social proof ("Loved by 1M+ Viewers")'].map((item, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5 }}>
                    <ListItemText primary={`• ${item}`} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
          <Grid item xs={12} md={6}>
            <Paper elevation={2} sx={{ p: 3, height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <VisibilityIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="h3" sx={{ fontSize: '1.3rem', mt: 0 }}>Visual Impact</Typography>
              </Box>
              <Typography variant="body1">
                How titles appear in search results affects click-through rates:
              </Typography>
              <List dense>
                {['Front-load important keywords',
                  'Use title case capitalization',
                  'Include symbols like [ ] or | for separation',
                  'Keep mobile display in mind (shorter titles work better)'].map((item, idx) => (
                  <ListItem key={idx} sx={{ py: 0.5 }}>
                    <ListItemText primary={`• ${item}`} />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
<br></br>
<br></br>
        <Typography variant="h2">Why YouTube Titles Matter More Than Ever</Typography>
        
        <Typography variant="body1" sx={{ mb: 3 }}>
          In 2024, YouTube's algorithm has become increasingly sophisticated in evaluating title quality. A well-crafted title:
        </Typography>
        
        <Grid container spacing={2} sx={{ mb: 4 }}>
          {[
            { icon: <SearchIcon color="primary" />, title: 'Boosts Discoverability', desc: 'Helps your video appear in both search and recommended feeds' },
            { icon: <TrendingUpIcon color="primary" />, title: 'Increases CTR', desc: 'Compelling titles can double or triple your click-through rate' },
            { icon: <ThumbUpIcon color="primary" />, title: 'Sets Expectations', desc: 'Proper titles lead to better audience retention and satisfaction' },
            { icon: <LightbulbIcon color="primary" />, title: 'Enhances SEO', desc: 'Titles are the primary ranking factor in YouTube search' }
          ].map((item, idx) => (
            <Grid item xs={12} sm={6} md={3} key={idx}>
              <Paper elevation={2} sx={{ p: 2, height: '100%', display: 'flex', flexDirection: 'column' }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  {item.icon}
                  <Typography variant="h3" sx={{ fontSize: '1.1rem', mt: 0, ml: 1 }}>{item.title}</Typography>
                </Box>
                <Typography variant="body2" sx={{ mt: 'auto' }}>{item.desc}</Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
<br></br>
        <Typography variant="h2">Real-World Title Transformations</Typography>
        
        <Typography variant="body1" sx={{ mb: 3 }}>
          See how small tweaks to titles made massive differences for these channels:
        </Typography>
        
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {titleCaseStudies.map((study, idx) => (
            <Grid item xs={12} md={4} key={idx}>
              <Paper elevation={3} sx={{ p: 2, height: '100%', borderTop: '3px solid #1976d2' }}>
                <Typography variant="h3" sx={{ fontSize: '1.2rem', color: 'text.secondary' }}>Before:</Typography>
                <Typography variant="body1" sx={{ mb: 2, fontStyle: 'italic' }}>"{study.before}"</Typography>
                <Typography variant="h3" sx={{ fontSize: '1.2rem', color: 'success.main' }}>After:</Typography>
                <Typography variant="body1" sx={{ mb: 2, fontWeight: 500 }}>"{study.after}"</Typography>
                <Chip label={`Result: ${study.improvement}`} color="success" variant="outlined" />
              </Paper>
            </Grid>
          ))}
        </Grid>
<br></br>
        <Typography variant="h2">How Our YouTube Title Generator Works</Typography>
        
        <Typography variant="body1" sx={{ mb: 3 }}>
          Our AI analyzes thousands of high-performing videos to recommend titles optimized for both viewers and algorithms:
        </Typography>
        
        <Box component="ol"  sx={{ paddingLeft: 4, marginBottom: 3 }}>
          {[
            'Visit our <Link href="https://www.myfreshtools.com/youtube/video-title-generator" target="_blank" color="primary">YouTube Title Generator</Link>',
            'Enter your video topic or focus keyword (e.g., "vegan meal prep")',
            'Select your content category and preferred tone (informative, fun, dramatic, etc.)',
            'Choose whether you want question-based, listicle, or tutorial-style titles',
            'Click "Generate Titles" to receive multiple optimized options',
            'Refine your selection by adding filters like length, emotional tone, or power words',
            'Copy your favorite title directly to your video or export for later use'
          ].map((step, idx) => (
            <li key={idx} dangerouslySetInnerHTML={{ __html: step }} />
          ))}
        </Box>

        <Box sx={{ 
          backgroundColor: '#f8f9fa',
          p: 3,
          borderRadius: 2,
          mb: 4,
          borderLeft: '4px solid #ff9800'
        }}>
          <Typography variant="h3" sx={{ mt: 0, display: 'flex', alignItems: 'center' }}>
            <ChecklistIcon color="warning" sx={{ mr: 1 }} /> Pro Tip: Title Optimization Checklist
          </Typography>
          <List dense>
            {[
              'Does your title contain your main keyword in the first 3 words?',
              'Is it clear what value the video provides?',
              'Does it create curiosity without being clickbaity?',
              'Is it under 60 characters for mobile display?',
              'Does it match the content accurately?'
            ].map((item, idx) => (
              <ListItem key={idx} sx={{ py: 0.5 }}>
                <ListItemText primary={`✓ ${item}`} />
              </ListItem>
            ))}
          </List>
        </Box>

        <Typography variant="h2">Advanced Title Strategies</Typography>
        
        <Accordion elevation={2} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h3" sx={{ fontSize: '1.2rem' }}>Seasonal and Trending Title Tactics</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Capitalize on current events, holidays, and trends by incorporating timely references. For example:
              <Box component="ul" sx={{ pl: 4, mt: 1 }}>
                <li>"2024 iPhone 15 Pro Max Review: Worth the Upgrade?"</li>
                <li>"Valentine's Day Makeup Tutorial That Will Steal Hearts"</li>
                <li>"Summer Body Workout Challenge (Start Now!)"</li>
              </Box>
              Our tool automatically suggests trending keywords to include in your titles.
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Accordion elevation={2} sx={{ mb: 2 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h3" sx={{ fontSize: '1.2rem' }}>Localization Strategies</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              For location-specific content, include geographic markers:
              <Box component="ul" sx={{ pl: 4, mt: 1 }}>
                <li>"Best Pizza in Chicago: Our Top 5 Picks"</li>
                <li>"Tokyo Travel Guide: Hidden Gems Only Locals Know"</li>
                <li>"New York Style Bagels You Can Make at Home"</li>
              </Box>
              This helps capture local search traffic and makes your content more relevant to specific audiences.
            </Typography>
          </AccordionDetails>
        </Accordion>
<br></br>
        <Typography variant="h2">Frequently Asked Questions</Typography>
        
        <Accordion elevation={0} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h3" sx={{ fontSize: '1.1rem' }}>How often should I update my video titles?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              We recommend reviewing titles every 3-6 months. If a video isn't performing well, try A/B testing a new title. For evergreen content, seasonal updates can help maintain relevance. Our tool tracks performance and can suggest when a title refresh might help.
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Accordion elevation={0} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h3" sx={{ fontSize: '1.1rem' }}>Should my title match my thumbnail text?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              Not exactly. Your title and thumbnail should complement each other but not duplicate information. For example, if your title says "5 Easy Pasta Recipes," your thumbnail might show delicious pasta with text like "Ready in 15 Minutes!" This creates multiple points of interest.
            </Typography>
          </AccordionDetails>
        </Accordion>
        
        <Accordion elevation={0} sx={{ mb: 1 }}>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h3" sx={{ fontSize: '1.1rem' }}>How do I balance SEO and clickability?</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Typography>
              The best titles work for both algorithms and humans. Start with your primary keyword, then add emotional or curiosity elements. For example, instead of "Yoga for Back Pain," try "Relieve Back Pain FAST With These 3 Yoga Poses (Works Immediately!)" Our generator automatically finds this balance for you.
            </Typography>
          </AccordionDetails>
        </Accordion>

        <Divider sx={{ my: 4 }} />

        <Box sx={{ 
          backgroundColor: 'primary.main',
          color: 'white',
          p: 4,
          borderRadius: 2,
          textAlign: 'center',
          background: 'linear-gradient(135deg, #1976d2 0%, #0d47a1 100%)'
        }}>
          <Typography variant="h3" sx={{ mt: 0, mb: 2 }}>
            Ready to Transform Your YouTube Titles?
          </Typography>
          <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.9)' }}>
            Join thousands of creators who've increased their views by 200% or more with our data-driven title suggestions.
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            href="https://www.myfreshtools.com/youtube/video-title-generator"
            target="_blank"
            rel="noopener noreferrer"
            sx={{ 
              backgroundColor: 'white',
              color: 'primary.main',
              fontSize: '1.1rem',
              py: 1.5,
              px: 4,
              fontWeight: 600,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.9)'
              }
            }}
            startIcon={<TitleIcon />}
          >
            Generate High-Performing Titles Now
          </Button>
          <Typography variant="body2" sx={{ mt: 2, fontStyle: 'italic', color: 'rgba(255,255,255,0.7)' }}>
            No credit card required - start for free today
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ mt: 4, fontStyle: 'italic' }}>
          Last updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
        </Typography>
      </Paper>
    </Container>
  </ThemeProvider>
);

export default YouTubeTitleGeneratorBlog;